// Maßnahmen-Registry: zu einem Problem-/Critical-Zustand die anwendbaren
// "was kann ich JETZT tun"-Aktionen liefern -- jede mit einer Klartext-
// Beschreibung ihrer Konsequenzen. Gedacht als der menschengeführte
// Recovery-Pfad zur kritischen Lane (critical_events_lane): ein Critical ist
// nicht wegklickbar, aber über eine Maßnahme (mit bewusst gemachten Folgen)
// schnell lösbar.
//
// Erste registrierte Maßnahme: ein Cluster befristet stummschalten
// (entity_disables, Domain "cluster"). Dieselbe Registry speist sowohl den
// Inline-Trigger auf der Cluster-/Hosts-Seite als auch die Maßnahmen-
// Affordance an einem kritischen Event auf dem Dashboard.

import {
  disableEntity,
  enableEntity,
  entityDisableExpiry,
  isEntityDisabled,
} from "./entity-disables.svelte";
import { t } from "./i18n.svelte";

// RemediationVariant = eine konkrete Ausprägung einer Maßnahme, z.B. die
// Dauer beim Stummschalten. seconds === 0 heißt "unbegrenzt".
export interface RemediationVariant {
  key: string;
  label: string;
  seconds: number;
}

// Remediation = eine anwendbare Maßnahme. Hat entweder genau eine Aktion
// (variants leer -> run() ohne Argument) oder mehrere Ausprägungen
// (variants gesetzt -> run(variant)). consequence ist der Klartext, der dem
// Nutzer VOR dem Klick sagt, was passiert.
export interface Remediation {
  id: string;
  label: string;
  consequence: string;
  // "danger" für scharfschaltende/folgenreiche Aktionen, "accent" für die
  // entschärfende Gegenaktion (Re-Aktivieren). Default neutral.
  tone?: "accent" | "danger";
  variants?: RemediationVariant[];
  run: (variant?: RemediationVariant) => Promise<void>;
}

// Dauer-Presets fürs Stummschalten. 0 = unbegrenzt.
function muteVariants(): RemediationVariant[] {
  return [
    { key: "1h", label: t("cluster.disable.dur_1h", "1 Stunde"), seconds: 3600 },
    { key: "1d", label: t("cluster.disable.dur_1d", "1 Tag"), seconds: 86400 },
    { key: "1w", label: t("cluster.disable.dur_1w", "1 Woche"), seconds: 604800 },
    { key: "inf", label: t("cluster.disable.dur_inf", "unbegrenzt"), seconds: 0 },
  ];
}

// clusterRemediations liefert die Maßnahmen für ein konkretes Cluster --
// reaktiv über isEntityDisabled(): ist es schon stillgelegt, gibt es nur die
// Gegenaktion (Re-Aktivieren), sonst das Stummschalten mit Dauer-Presets.
// In einem $derived der UI aufgerufen, damit der Wechsel live umschlägt.
export function clusterRemediations(
  clusterId: string,
  clusterName: string,
): Remediation[] {
  if (isEntityDisabled("cluster", clusterId)) {
    return [
      {
        id: `cluster-reactivate:${clusterId}`,
        label: t("remediation.cluster_reactivate", "{name} wieder aktivieren").replace(
          "{name}",
          clusterName,
        ),
        consequence: t(
          "remediation.cluster_reactivate_consequence",
          "Überwachung sofort wieder scharf: Offline-Alarme, Tasks und Auto-Deploy laufen normal weiter.",
        ),
        tone: "accent",
        run: async () => {
          await enableEntity("cluster", clusterId);
        },
      },
    ];
  }
  return [
    {
      id: `cluster-mute:${clusterId}`,
      label: t("remediation.cluster_mute", "{name} stummschalten").replace(
        "{name}",
        clusterName,
      ),
      consequence: t(
        "remediation.cluster_mute_consequence",
        "Für die ganze Insel: keine Offline-Alarme, Tasks dorthin (z.B. Backups) werden übersprungen statt fehlzuschlagen, Auto-Deploy pausiert. Rüstet sich nach Ablauf automatisch wieder scharf.",
      ),
      tone: "danger",
      variants: muteVariants(),
      run: async (variant) => {
        const seconds = variant?.seconds ?? 0;
        const expiresAt =
          seconds > 0 ? new Date(Date.now() + seconds * 1000).toISOString() : "";
        await disableEntity(
          "cluster",
          clusterId,
          t("cluster.disable.reason", "manuell deaktiviert"),
          expiresAt,
        );
      },
    },
  ];
}

// clusterExpiry: ISO-Ablaufzeit eines stillgelegten Clusters ("" = unbegrenzt
// oder nicht deaktiviert). Bequemer Re-Export fürs "deaktiviert bis"-Label,
// damit Consumer nur dieses Modul importieren müssen.
export function clusterExpiry(clusterId: string): string {
  return entityDisableExpiry("cluster", clusterId);
}

// RemediationClusterHint = der vom Backend pro Critical-Event aufgelöste
// Cluster (host_offline/task_failed -> stummschaltbares Cluster). Die
// Auflösung liegt bewusst im Backend (target_hosts ist SoT), nicht im FE.
export interface RemediationClusterHint {
  cluster_id: string;
  cluster_name: string;
}

// remediationsForCritical übersetzt den Backend-Hinweis eines kritischen
// Events in die anwendbaren Maßnahmen. Kein Hinweis -> [] (keine Affordance).
// Uniform für host_offline UND task_failed -- die Zuordnung Entity->Cluster
// hat das Backend schon gemacht.
export function remediationsForCritical(
  hint: RemediationClusterHint | undefined,
): Remediation[] {
  if (!hint?.cluster_id) return [];
  return clusterRemediations(hint.cluster_id, hint.cluster_name || hint.cluster_id);
}
